import { useState } from 'react';
import useSWR from 'swr';
import { useAuthStore } from '../../hooks/useAuth';
import { 
    ArrowLeft, 
    CheckCircle2, 
    XCircle, 
    AlertCircle, 
    Package,
    Loader2,
    CalendarClock,
    FileCode2,
    FileSpreadsheet,
    FileText,
    Image as ImageIcon
} from 'lucide-react';
import { format } from 'date-fns';

interface ImportSessionDetail {
    id: string;
    sourceType: string | null;
    sourceFileName: string | null;
    sourceFileSize: number | null;
    sourceAvailable: boolean;
    sourceArchiveWarning: string | null;
    visualFallbackUsed: boolean;
    createdAt: string;
    createdByName: string | null;
    rows: ImportSessionRow[];
}

interface ImportSessionRow {
    id: string;
    rowIndex: number;
    importedPoNumber: string | null;
    finalPoNumber: string | null;
    importedClientName: string | null;
    resolvedCustomerName: string | null;
    status: 'created' | 'skipped' | 'failed' | 'duplicate' | 'unresolved_customer' | 'missing_required';
    warnings: string[];
    errors: string[];
    aliasCreated: boolean;
}

const getSourceIcon = (type: string | null) => {
    switch (type) {
        case 'csv': return <FileCode2 size={20} className="text-blue-500" />;
        case 'excel': return <FileSpreadsheet size={20} className="text-green-600" />;
        case 'text_pdf': return <FileText size={20} className="text-red-500" />;
        case 'visual_pdf': return <ImageIcon size={20} className="text-purple-500" />;
        case 'pasted_text': return <FileText size={20} className="text-slate-500" />;
        default: return <FileText size={20} className="text-slate-400" />;
    }
};

const getSourceLabel = (type: string | null) => {
    switch (type) {
        case 'csv': return 'CSV File';
        case 'excel': return 'Excel File';
        case 'text_pdf': return 'Text PDF';
        case 'visual_pdf': return 'Visual PDF (AI)';
        case 'pasted_text': return 'Pasted Text';
        default: return 'Unknown Source';
    }
};

export function POImportHistoryDetail({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
    const { token } = useAuthStore();

    const { data: session, error, isLoading } = useSWR<ImportSessionDetail>(
        sessionId ? [`/api/warehouse-pos/import/history/${sessionId}`, token] : null,
        async ([url, t]) => {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${t}` }
            });
            if (!res.ok) throw new Error('Failed to load session details');
            return res.json();
        }
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64 bg-white border border-slate-200 rounded-2xl">
                <Loader2 className="animate-spin text-blue-500" size={32} />
            </div>
        );
    }

    if (error || !session) {
        return (
            <div className="text-center p-8 bg-red-50 text-red-600 rounded-xl border border-red-200">
                <div className="flex flex-col items-center gap-2">
                    <XCircle size={24} />
                    <p className="font-semibold">Failed to load import details</p>
                    <button onClick={onBack} className="text-sm underline mt-2">Back to history</button>
                </div>
            </div>
        );
    }

    const createdCount = session.rows.filter(r => r.status === 'created').length;
    const skippedCount = session.rows.filter(r => ['skipped', 'duplicate'].includes(r.status)).length;
    const failedCount = session.rows.filter(r => ['failed', 'unresolved_customer', 'missing_required'].includes(r.status)).length;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={onBack}
                    className="p-2 -ml-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        Import Session
                        <span className="font-mono text-sm text-slate-500 font-normal bg-slate-100 px-2 py-0.5 rounded">
                            {session.id.split('_')[1]?.substring(0, 8)}
                        </span>
                    </h2>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                            <CalendarClock size={14} />
                            {format(new Date(session.createdAt), 'MMM d, yyyy h:mm a')}
                        </span>
                        <span>•</span>
                        <span>By {session.createdByName || 'System'}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center shrink-0">
                        {getSourceIcon(session.sourceType)}
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</p>
                        <p className="text-sm font-bold text-slate-900">
                            {getSourceLabel(session.sourceType)}
                        </p>
                        {session.sourceFileName && (
                            <p className="text-xs text-slate-500 truncate" title={session.sourceFileName}>
                                {session.sourceFileName} 
                                {session.sourceFileSize && (
                                    <span className="ml-1 text-slate-400">
                                        ({(session.sourceFileSize / 1024).toFixed(1)} KB)
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4 border border-green-200 flex flex-col justify-center">
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">Created</p>
                    <p className="text-2xl font-bold text-green-700">{createdCount}</p>
                </div>

                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 flex flex-col justify-center">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Skipped</p>
                    <p className="text-2xl font-bold text-amber-700">{skippedCount}</p>
                </div>

                <div className="bg-red-50 rounded-xl p-4 border border-red-200 flex flex-col justify-center">
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Failed</p>
                    <p className="text-2xl font-bold text-red-700">{failedCount}</p>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-semibold text-slate-700 text-sm">
                    Processed Rows
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-white border-b border-slate-100">
                                <th className="px-4 py-3 font-semibold text-slate-500 w-16">Row</th>
                                <th className="px-4 py-3 font-semibold text-slate-500">PO Number</th>
                                <th className="px-4 py-3 font-semibold text-slate-500">Customer</th>
                                <th className="px-4 py-3 font-semibold text-slate-500">Status</th>
                                <th className="px-4 py-3 font-semibold text-slate-500">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {session.rows.map((row) => (
                                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3 text-slate-500 tabular-nums">
                                        #{row.rowIndex + 1}
                                    </td>
                                    <td className="px-4 py-3 font-medium text-slate-900">
                                        {row.finalPoNumber || row.importedPoNumber || <span className="text-slate-400 italic">Missing</span>}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">
                                        {row.resolvedCustomerName || row.importedClientName || <span className="text-slate-400 italic">Unknown</span>}
                                        {row.aliasCreated && (
                                            <span className="ml-2 inline-flex text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">
                                                Alias Saved
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {row.status === 'created' ? (
                                            <span className="inline-flex items-center gap-1.5 text-green-700 font-medium">
                                                <CheckCircle2 size={14} className="text-green-500" />
                                                Created
                                            </span>
                                        ) : ['skipped', 'duplicate'].includes(row.status) ? (
                                            <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium">
                                                <AlertCircle size={14} className="text-amber-500" />
                                                {row.status === 'duplicate' ? 'Duplicate' : 'Skipped'}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-red-700 font-medium">
                                                <XCircle size={14} className="text-red-500" />
                                                Failed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {row.errors && row.errors.length > 0 ? (
                                            <ul className="list-disc list-inside text-red-600">
                                                {row.errors.map((e, i) => <li key={i}>{e}</li>)}
                                            </ul>
                                        ) : row.warnings && row.warnings.length > 0 ? (
                                            <ul className="list-disc list-inside text-amber-600">
                                                {row.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                            </ul>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {session.rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                        No rows were processed in this session.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
